import { useEffect, useState } from 'react'
import { useBeneficiariesQuery } from 'hooks/queries/useBeneficiariesQuery'
import { BeneficiaryDetails, BeneficiaryType } from 'types/beneficiaries'
import { mapPaymentMethodToBenficiaryDetails } from 'utils/beneficiaries'
import { useGetPaymentMethods } from '../queries/Beneficiaries/useGetPaymentMethods'

interface UseGetFiatBeneficiariesProps {
  beneficiaryType: BeneficiaryType
  currency: string
}

export const useGetFiatBeneficiaries = ({
  beneficiaryType,
  currency,
}: UseGetFiatBeneficiariesProps) => {
  const [fiatBeneficiaries, setFiatBeneficiaries] = useState<BeneficiaryDetails[]>([])
  const {
    data: paymentMethods,
    isFetching: isFetchingPaymentMethods,
    isError: isFetchingPaymentMethodsError,
    refetch: refetchPaymentMethods,
  } = useGetPaymentMethods({ enabled: false })

  const {
    data: { content: beneficiaries } = {},
    isFetching: isFetchingBeneficiaries,
    isError: isFetchingBeneficiariesError,
    refetch: refetchBeneficiaries,
  } = useBeneficiariesQuery(
    {
      sort: 'businessName',
      size: 1000,
      beneficiaryType,
      currency,
    },
    false
  )

  useEffect(() => {
    if (currency && beneficiaryType) {
      refetchBeneficiaries()

      if (beneficiaryType === BeneficiaryType.FIRST_PARTY) {
        refetchPaymentMethods()
      }
    }
  }, [beneficiaryType, currency])

  useEffect(() => {
    if (
      beneficiaryType === BeneficiaryType.FIRST_PARTY &&
      !!paymentMethods?.length &&
      !!beneficiaries?.length &&
      !isFetchingPaymentMethods &&
      !isFetchingBeneficiaries
    ) {
      const mappedBeneficiaries = paymentMethods
        ?.filter(paymentMethod => paymentMethod?.currency === currency)
        ?.map(paymentMethod => {
          const matchingBeneficiary = beneficiaries?.find(
            beneficiary => beneficiary?.reference === paymentMethod?.reference
          )

          if (!matchingBeneficiary) {
            return mapPaymentMethodToBenficiaryDetails(paymentMethod)
          }

          return matchingBeneficiary
        })
      setFiatBeneficiaries(mappedBeneficiaries)
    } else if (beneficiaries?.length && !isFetchingBeneficiaries) {
      setFiatBeneficiaries(beneficiaries)
      return
    }
  }, [
    beneficiaryType,
    paymentMethods,
    beneficiaries,
    isFetchingBeneficiaries,
    isFetchingPaymentMethods,
  ])

  return {
    fiatBeneficiaries,
    isFetching: isFetchingPaymentMethods || isFetchingBeneficiaries,
    isError: isFetchingPaymentMethodsError ?? isFetchingBeneficiariesError,
  }
}
